/**
 * client/src/services/lexistarPerformance.ts
 *
 * The LexiStar Fleet & targets API (ST-00247 availability and utilisation per truck, ST-00248
 * performance targets and actual against target). Shapes: `types/lexistar-performance`. Server:
 * server/modules/lexistar/performance, mounted at /api/lexistar.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response BODY.
 * Days are SAST days `YYYY-MM-DD` (toApiDateString from utils/dateUtils).
 *
 *   getFleetAvailability(query)     GET  /fleet-availability     per truck and period, the fleet's totals
 *   listTargets(query)              GET  /targets                every version + the metric catalogue
 *   getTargetScopes()               GET  /targets/scopes         the routes and trucks a target can apply to
 *   createTarget(input)             POST /targets                → target (409 code overlap: `conflict`)
 *   reviseTarget(id, body)          POST /targets/:id:revise     → { target, previous } (a new value from a day)
 *   updateTarget(id, patch)         PATCH /targets/:id           → target (when it ends, notes)
 *   retireTarget(id, body)          POST /targets/:id:retire     → target
 *   getTargetProgress(query)        GET  /targets/progress       actual against target per period
 */
import api from './api';
import type {
  FleetAvailabilityResponse,
  FleetGrain,
  PerformanceTarget,
  TargetInput,
  TargetList,
  TargetMetricKey,
  TargetPatch,
  TargetProgressResponse,
  TargetResult,
  TargetRetire,
  TargetRevision,
  TargetScopes,
  TargetScopeType,
} from '../types/lexistar-performance';

const LX = '/lexistar';
const target = (id: string): string => `${LX}/targets/${encodeURIComponent(id)}`;

/** Query parameters without the empty ones; arrays as comma lists. */
function params(q: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
    } else out[k] = String(v);
  }
  return out;
}

export interface FleetQuery {
  from?: string;
  to?: string;
  grain?: FleetGrain;
  asset?: string[];
  tenant_id?: number;
}

export async function getFleetAvailability(query: FleetQuery = {}): Promise<FleetAvailabilityResponse> {
  const res = await api.get(`${LX}/fleet-availability`, { params: params({ ...query }) });
  return res.data as FleetAvailabilityResponse;
}

export async function listTargets(
  query: { metric?: TargetMetricKey[]; scope_type?: TargetScopeType; scope_id?: string; include_retired?: boolean } = {}
): Promise<TargetList> {
  const res = await api.get(`${LX}/targets`, { params: params({ ...query }) });
  return res.data as TargetList;
}

export async function getTargetScopes(): Promise<TargetScopes> {
  const res = await api.get(`${LX}/targets/scopes`);
  return res.data as TargetScopes;
}

export async function createTarget(input: TargetInput): Promise<PerformanceTarget> {
  const res = await api.post(`${LX}/targets`, input);
  return (res.data as { target: PerformanceTarget }).target;
}

export async function reviseTarget(id: string, body: TargetRevision): Promise<TargetResult> {
  const res = await api.post(`${target(id)}:revise`, body);
  return res.data as TargetResult;
}

export async function updateTarget(id: string, patch: TargetPatch): Promise<PerformanceTarget> {
  const res = await api.patch(target(id), patch);
  return (res.data as { target: PerformanceTarget }).target;
}

export async function retireTarget(id: string, body: TargetRetire): Promise<PerformanceTarget> {
  const res = await api.post(`${target(id)}:retire`, body);
  return (res.data as { target: PerformanceTarget }).target;
}

export interface ProgressQuery {
  from?: string;
  to?: string;
  grain?: FleetGrain;
  metric?: TargetMetricKey[];
  scope_type?: TargetScopeType;
  scope_id?: string;
}

export async function getTargetProgress(query: ProgressQuery = {}): Promise<TargetProgressResponse> {
  const res = await api.get(`${LX}/targets/progress`, { params: params({ ...query }) });
  return res.data as TargetProgressResponse;
}
